import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import { useShittyMapStore } from '@/hooks/useShittyMapStore';
import getIpfsImageUrl from '@/utils/getIpfsImageUrl';
import formatNumber from '@/utils/formatNumber';

function StatBox({ label, value }) {
  return (
    <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700/30">
      <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">{label}</div>
      <div className="text-white font-bold text-lg">{value}</div>
    </div>
  );
}

export default function ShittyMapDetailPanel() {
  const { selectedCity, detailPanelOpen, setDetailPanelOpen, setSelectedCity } = useShittyMapStore();

  const handleClose = () => {
    setDetailPanelOpen(false);
    setSelectedCity(null);
  };

  if (!selectedCity) return null;

  const meta = selectedCity.metadata?.properties;
  const cityName = selectedCity.details?.name || selectedCity.params?.name || `Shitty City #${selectedCity.index}`;
  const upgrade = meta?.Upgrade || 'None';
  const blockCount = meta?.['Block Count'] || 0;
  const imageUrl = selectedCity.cid
    ? getIpfsImageUrl(selectedCity.cid, { width: 800, quality: 85 })
    : null;

  return (
    <AnimatePresence>
      {detailPanelOpen && (
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 280 }}
          className="absolute top-0 right-0 bottom-0 w-full sm:w-[380px] bg-gray-900/95 backdrop-blur-xl shadow-2xl overflow-y-auto z-20 border-l border-primary-500/20"
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-gray-800/90 hover:bg-primary-500/20 border border-gray-700/50 hover:border-primary-500/50 transition-all duration-200"
            aria-label="Close panel"
          >
            <svg width={14} height={14} viewBox="0 0 16 16" fill="currentColor" className="text-gray-400 hover:text-white">
              <path d="M8 6.586L13.293 1.293a1 1 0 111.414 1.414L9.414 8l5.293 5.293a1 1 0 01-1.414 1.414L8 9.414l-5.293 5.293a1 1 0 01-1.414-1.414L6.586 8 1.293 2.707a1 1 0 011.414-1.414L8 6.586z" />
            </svg>
          </button>

          <div className="aspect-[50/35] bg-gray-800 relative overflow-hidden">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={cityName}
                className="w-full h-full object-cover"
                loading="eager"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-600">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/20 to-transparent" />
          </div>

          <div className="p-5 -mt-14 relative">
            <div className="inline-block bg-primary-500 text-gray-900 text-xs font-bold px-3 py-1 rounded-full mb-3 shadow-lg shadow-primary-500/25">
              {upgrade === 'None' ? 'No Upgrade' : upgrade}
            </div>

            <h2 className="text-2xl font-bold text-white mb-1">{cityName}</h2>
            <p className="text-gray-500 text-sm mb-5">City #{selectedCity.index}</p>

            <div className="grid grid-cols-2 gap-2.5 mb-5">
              <StatBox
                label="Rarity Score"
                value={formatNumber(selectedCity.rarityScore || 0)}
              />
              <StatBox
                label="Population"
                value={`${selectedCity.currentPopulation || 0}/${selectedCity.maxPopulation || 0}`}
              />
              <StatBox label="Blocks" value={blockCount} />
              <StatBox
                label="Citizens"
                value={selectedCity.citizens?.length || 0}
              />
            </div>

            {selectedCity.currentPopulation > 0 && selectedCity.maxPopulation > 0 && (
              <div className="mb-5">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Population</span>
                  <span className="text-primary-500 font-medium">
                    {Math.round((selectedCity.currentPopulation / selectedCity.maxPopulation) * 100)}%
                  </span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden border border-gray-700/50">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(selectedCity.currentPopulation / selectedCity.maxPopulation) * 100}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className="h-full bg-gradient-to-r from-primary-600 to-primary-400 rounded-full"
                  />
                </div>
              </div>
            )}

            <Link href={`/cities/${selectedCity.index}`}>
              <a className="block w-full bg-primary-500 hover:bg-primary-400 text-gray-900 font-bold py-3 px-4 rounded-lg text-center transition-all duration-200 shadow-lg shadow-primary-500/20 hover:shadow-primary-500/40">
                View Full Details
              </a>
            </Link>

            <button
              onClick={handleClose}
              className="block w-full mt-2.5 bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700/50 text-gray-300 font-medium py-2.5 px-4 rounded-lg text-center transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
